import React, {useState} from "react";
import {Box, Form, FormField, Text, TextInput, ResponsiveContext} from "grommet";
import { useRouter } from 'next/router'
import {getSession, signIn} from "next-auth/react";
import NextAnchor from "../../components/NextAnchor";
import NarrowHeading from "../../components/NarrowHeading";
import {handleErrors} from "../../utils/errors";
import StatusMessage from "../../components/StatusMessage";
import { debounce } from "../../utils/debounce";
import LoginPageLayout from "../../components/layouts/LoginPageLayout";
import {identifyUser} from "../../utils/segment";
import Loading from "../../components/Loading";
import Confirm from "../../components/Confirm";
import Button from "../../components/Button";

const Signup = () => {
    const [authError, setAuthError] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const router = useRouter();
    const { source, token, email, confirmation } = router.query;
    const [formValues, setFormValues] = useState({
        email: email || "",
    });
    const [emailCopy, setEmailCopy] = useState();
    const [passwordCopy, setPasswordCopy] = useState();
    // emailValidationRegex ref: https://gist.github.com/markhowellsmead/48f4f62acd7f75afaceaffab430c0345

    let emailValidationRegex = new RegExp(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
    let tryAgainMessage = "Please enter a valid email address";

    const onSubmit = ({ value }) => {
        setIsLoading(true);
        let body = { ...value, token: token, source: source };
        setEmailCopy(value?.email);
        setPasswordCopy(value?.password);
        fetch("/api/signup/new", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        })
            .then(handleErrors)
            .catch((e) => {
                setIsLoading(false);
                setAuthError(e);
            })
            .then((data) => {
                if (data) {
                    if (data.status === 200) {
                        const credentials = (({ email, password }) => ({ email, password }))(value);
                        const redirectTo = "/signup/business";
                        const postData = { ...credentials, callbackUrl: redirectTo, redirect: false };
                        signIn("credentials", postData)
                            .then(async (data) => {
                                if (data.error) return;
                                if (data.status === 200) {
                                    const { user } = await getSession();
                                    if (user) identifyUser(user);
                                    return router.push(data?.url || redirectTo);
                                }
                            })
                            .catch((error) => {
                                setIsLoading();
                            });
                    } else if (data && data.status === 201) {
                        router.push("/signup?confirmation=true")
                        setIsLoading();
                    }
                }
            })
    };

    return (
        <>
            {confirmation ? (
                <Confirm email={emailCopy} password={passwordCopy} />
            ) : (
                <LoginPageLayout title="Signup">
                    <NarrowHeading
                        heading="Create an account"
                        paragraph="You will need to have a registered business and provide ownership information."
                    />

                    <Box margin={{ vertical: "xsmall" }}>
                        {authError && <StatusMessage apiError={authError} />}
                        <Form
                            value={formValues}
                            onChange={setFormValues}
                            onSubmit={debounce(onSubmit, 300)}
                            messages={{
                                required: "This field is required",
                            }}
                        >
                            <ResponsiveContext.Consumer>
                                {(size) => (
                                    <Box direction="row-responsive" gap={size === "small" ? "none" : "small"}>
                                        <FormField
                                            placeholder="Legal first name*"
                                            name="first_name"
                                            required
                                            component={TextInput}
                                            data-testid="signup-form-fname"
                                        />
                                        <FormField
                                            placeholder="Legal last name*"
                                            name="last_name"
                                            component={TextInput}
                                            required
                                            data-testid="signup-form-lname"
                                        />
                                    </Box>
                                )}
                            </ResponsiveContext.Consumer>
                            <FormField
                                placeholder="Work email*"
                                name="email"
                                autocomplete="off"
                                component={TextInput}
                                validate={{
                                    regexp: emailValidationRegex,
                                    message: tryAgainMessage,
                                }}
                                required
                                data-testid="signup-form-email"
                            />
                            <FormField
                                type="password"
                                placeholder="Create password* (min. 8 characters)"
                                name="password"
                                autocomplete="off"
                                component={TextInput}
                                validate={(password) => {
                                    if (password && password.length < 8)
                                        return "Please enter a password greater than 8 characters";
                                    return undefined;
                                }}
                                data-testid="signup-form-password"
                                required
                            />
                            <Box pad={{ vertical: "medium", horizontal: "none" }}>
                                <Button
                                    event={{section: 'sign_up_form', element: 'start_application'}}
                                    type="submit"
                                    primary
                                    label={isLoading ? <Loading plain color="magento" /> : "Start application"}
                                    data-testid="signup-form-submit"
                                    disabled={isLoading}
                                />
                                <Box pad={{ top: "xsmall" }} align="center">
                                    <Text size="xsmall" color="text-weak">
                                        2 minutes to complete. No credit card required
                                    </Text>
                                </Box>
                            </Box>
                        </Form>
                        <Text textAlign="center">
                            <NextAnchor
                                href="/session/signin"
                                primary
                                label="Already have an account?"
                                data-testid="sign-in-link"
                            />
                        </Text>
                    </Box>
                </LoginPageLayout>
            )}
        </>
    );
};

export default Signup;

Signup.auth = false;
